// # Button · IconButton — 모든 단추의 한 모양(docs/design.md)
//
// 변형과 크기는 클래스가 아니라 `data-variant`·`data-size` 로 싣는다. CSS 선택자가 cva 의
// 일을 하고, 시험은 속성 하나로 어떤 모양인지 읽는다.
//
// - `type` 의 기본은 `button` 이다. 날 `<button>` 의 기본(`submit`)은 폼 안에서 누르는 단추마다
//   폼을 보내 버린다. 보내기 단추는 `type="submit"` 을 적는다.
// - 로딩 중에는 누를 수 없고 스크린리더에 `aria-busy` 로 알린다. 글자는 그대로 두고
//   아이콘 자리에 도는 표시를 얹는다 — 글자가 사라지면 단추 폭이 흔들린다. 잠금은 `disabled` 가 아니라
//   `aria-disabled` 다 — 초점이 앉은 단추가 `disabled` 가 되면 브라우저가 초점을 문서 바닥으로 떨어뜨려,
//   [저장] 을 누른 키보드 사용자가 매번 처음부터 Tab 해야 했다(3차 F290). 누름은 여기서 막는다.
//   `aria-disabled` 를 직접 주면 같은 뜻이다(초점은 받되 누르지 못하는 단추). 보내기 단추(`type="submit"`)는
//   `disabled` 도 같은 식으로 잠근다 — 보내고 나면 칸이 비어 단추가 꺼지는데, 방금 누른 초점이 거기 있어 바닥에
//   떨어졌다(초대 보내기·비밀번호 바꾸기·이모지 추가 …, 3차 F296).
// - 누르면 **다른 곳으로 가는** 것은 단추가 아니라 링크다. 모양은 같게, 요소는 `<a>` 로 —
//   `ButtonLink`. 새 탭·내려받기·가운데 클릭이 브라우저 기본대로 된다. 글 안의 이동은
//   `link` 변형(배경·테두리 없는 강조색 글자), 문장 속 날 `<a>` 는 `.Link` 클래스다.
// - 아이콘만 있는 단추는 `IconButton` 이고 `label` 이 **필수**다. 그림만 있는 단추는 스크린리더가
//   "단추" 라고만 읽는다. 같은 글이 kit `Tooltip` 풍선으로 뜨고, 단축키가 있으면 `shortcut` 으로 곁에
//   적는다. 네이티브 `title` 은 받지 않는다 — 늦게 뜨고, 키보드로는 안 뜨고, 모양이 OS 마다 다르다.
// - 켜고 끄는 단추(음소거·즐겨찾기·담기·반응)는 `pressed` 를 준다. `aria-pressed` 로 읽히고 모양(강조색 옅은
//   바탕·글자)은 kit 이 한 벌로 칠한다 — 화면마다 눌린 모양을 따로 칠하면 같은 뜻이 네 가지로 보인다(G9).
// - 아이콘 크기는 `ICON_SIZE`(아이콘만)·`LEAD_SIZE`(글자 앞) 둘이다. `IconButton` 안의 아이콘은 CSS(`kit.css`)가 그 크기로 맞추므로
//   부르는 쪽은 `size` 를 적지 않는다.
//
// 기본값은 구조분해 기본값이 아니라 `??` 로 준다 — React Compiler 가 그 모양에서 포기한다(AGENTS.md §2-2).

import { LoaderCircle } from 'lucide-react'
import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { cx } from './cx.ts'
import { Tooltip } from './tooltip.tsx'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** 기본 `secondary`. 한 화면에 `primary` 는 하나만. */
  variant?: ButtonVariant
  /** 기본 `md`(32px). */
  size?: ButtonSize
  /** 참이면 누를 수 없고 도는 표시가 아이콘 자리에 선다. */
  loading?: boolean
  /** 글자 앞 아이콘(lucide 요소). */
  icon?: ReactNode
  /** 켜고 끄는 단추의 지금 상태. 주면 `aria-pressed` 가 붙는다(안 주면 켜고 끄는 단추가 아니다). */
  pressed?: boolean
  ref?: Ref<HTMLButtonElement>
}

/** 아이콘만 있는 단추의 아이콘 크기 — 단추 크기와 한 쌍이다. `kit.css` 의 `--icon-size` 와 같은 값. */
export const ICON_SIZE: Record<ButtonSize, number> = { sm: 16, md: 18, lg: 20 }
/** 글자 앞 아이콘 크기 — 글자 곁이라 한 단계 작다. */
export const LEAD_SIZE: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 }

export function Button(props: ButtonProps) {
  const {
    variant,
    size,
    loading,
    icon,
    className,
    children,
    type,
    disabled,
    pressed,
    onClick,
    ...rest
  } = props
  const s = size ?? 'md'
  const locked = disabled === true && type === 'submit'
  const held =
    locked ||
    (disabled !== true &&
      (loading === true || rest['aria-disabled'] === true || rest['aria-disabled'] === 'true'))
  const lead = loading ? <LoaderCircle className="spin" size={LEAD_SIZE[s]} aria-hidden /> : icon
  return (
    <button
      {...rest}
      type={type ?? 'button'}
      className={cx('Button', className)}
      data-variant={variant ?? 'secondary'}
      data-size={s}
      disabled={locked ? undefined : disabled}
      aria-disabled={held || undefined}
      // 잠긴 단추의 누름은 폼 보내기까지 막는다(칸에서 Enter 도 이 누름을 거친다).
      onClick={held ? (e) => e.preventDefault() : onClick}
      aria-busy={loading || undefined}
      aria-pressed={pressed ?? rest['aria-pressed']}
    >
      {lead}
      {children}
    </button>
  )
}

export interface ButtonLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** 기본 `secondary`. */
  variant?: ButtonVariant
  /** 기본 `md`. */
  size?: ButtonSize
  /** 글자 앞 아이콘(lucide 요소). */
  icon?: ReactNode
  href: string
  ref?: Ref<HTMLAnchorElement>
}

/** 단추 모양의 링크. 가는 곳이 있으면 이것을 쓴다(날 `<a className="Button">` 금지). */
export function ButtonLink(props: ButtonLinkProps) {
  const { variant, size, icon, className, children, ...rest } = props
  return (
    <a
      {...rest}
      className={cx('Button', className)}
      data-variant={variant ?? 'secondary'}
      data-size={size ?? 'md'}
    >
      {icon}
      {children}
    </a>
  )
}

export interface IconButtonProps
  extends Omit<ButtonProps, 'icon' | 'children' | 'aria-label' | 'title'> {
  /** 스크린리더가 읽고 풍선에 뜨는 이름. 비울 수 없다. */
  label: string
  /** 그릴 아이콘(lucide 요소). 크기는 적지 않는다. */
  icon: ReactNode
  /** 풍선 곁에 적을 단축키("⌘B"). */
  shortcut?: string
  /** 풍선에 이름 대신 띄울 글 — 이름만으로 모자랄 때("5분까지"). */
  hint?: string
}

export function IconButton(props: IconButtonProps) {
  const { label, icon, variant, shortcut, hint, ...rest } = props
  return (
    <Tooltip content={hint ?? label} shortcut={shortcut}>
      <Button
        {...rest}
        variant={variant ?? 'ghost'}
        icon={icon}
        aria-label={label}
        data-icon-only=""
      />
    </Tooltip>
  )
}
